import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  type AutocompleteRenderGroupParams,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  ListItem,
  Typography,
} from "@mui/material";
import { CheckboxStatus } from "@root/utils/enums/checkbox";
import { type MouseEvent, type ReactNode, useState } from "react";
import { useAutocompleteAppellationsContext } from "./context";

export interface IAutocompleteAppellationsGroup {
  label: string;
  status: CheckboxStatus;
  enableCollapse?: boolean;
  noDivider?: boolean;
  onClick: (event: MouseEvent) => void;
  children: ReactNode;
}

export const AutocompleteAppellationsGroup = ({
  label,
  status,
  enableCollapse,
  noDivider,
  onClick,
  children,
}: IAutocompleteAppellationsGroup) => {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <>
      <ListItem sx={{ p: 0, display: "flex", justifyContent: "space-between", alignItems: "start", gap: 1 }}>
        <FormControlLabel
          onClick={onClick}
          control={
            <Checkbox
              checked={status === CheckboxStatus.CHECKED}
              indeterminate={status === CheckboxStatus.INDETERMINATE}
              size="small"
              sx={{ p: 0, mr: 1 }}
            />
          }
          label={
            <Typography
              variant="body1Bold"
              sx={{
                color: "black.dark",
              }}
            >
              {label}
            </Typography>
          }
          sx={{ alignItems: "start" }}
        />
        {enableCollapse && (
          <IconButton size="small" sx={{ mt: "-6px" }} onClick={() => setCollapsed((collapsed) => !collapsed)}>
            {collapsed ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
          </IconButton>
        )}
      </ListItem>
      {!collapsed && children}
      {!noDivider && <Divider sx={{ my: 1 }} />}
    </>
  );
};

interface AutocompleteAppellationsGroupForOptions {
  group: string;
  index: number;
  children: ReactNode;
}

const AutocompleteAppellationsGroupForOptions = ({
  group,
  children,
  index,
}: AutocompleteAppellationsGroupForOptions) => {
  const { groupsLength, getOptionGroupLabel, getOptionGroupStatus, handleClickOptionGroup } =
    useAutocompleteAppellationsContext();
  const status = getOptionGroupStatus(group);
  return (
    <AutocompleteAppellationsGroup
      label={getOptionGroupLabel(group)}
      status={status}
      noDivider={index >= groupsLength - 1}
      onClick={(evt) => handleClickOptionGroup(evt, group, status)}
    >
      {children}
    </AutocompleteAppellationsGroup>
  );
};

export const renderAutocompleteAppellationGroup = ({ group, children, key }: AutocompleteRenderGroupParams) => {
  return (
    <AutocompleteAppellationsGroupForOptions key={group} group={group} index={Number(key)}>
      {children}
    </AutocompleteAppellationsGroupForOptions>
  );
};
